import { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useQuery, useMutation } from '@apollo/client';
import Avatar from '../components/Avatar';
import {
  NavLink,
  Section,
  SectionItem,
  SectionLink,
  SectionName,
} from '../components/Chrome';
import {
  Drawer,
  DrawerExit,
  DrawerLink,
  DrawerList,
  DrawerTrigger,
} from '../components/Drawer';
import Grid from '../components/Grid';
import Inline from '../components/Inline';
import Loader from '../components/Loader';
import { A } from '../components/Markdown';
import { Tags, TagsItem } from '../components/Tags';
import Text from '../components/Text';
import { useToast } from '../components/Toast';
import Tooltip from '../components/Tooltip';
import Icon from '../components/Icon';
import ArchiveTopicModal from '../modals/ArchiveTopic';
import AssignTopicModal from '../modals/AssignTopic';
import DeleteCommentModal from '../modals/DeleteComment';
import EditCommentBodyModal from '../modals/EditCommentBody';
import EditTopicModal from '../modals/EditTopic';
import NewCommentModal from '../modals/NewComment';
import VersionsModal from '../modals/Versions';
import { VIEWER, SPACE, TOPIC } from '../utilities/queries';
import {
  PIN_TOPIC,
  UNPIN_TOPIC,
  UPDATE_TOPIC_STATE,
} from '../utilities/mutations';
import { getDate } from '../utilities/getDate';
import { getRelativeTime } from '../utilities/getRelativeTime';
import { useSpaceContext } from '../contexts/space';
import { useLayerContext } from '../contexts/layer';
import { markdown } from '../utilities/markdown';

const TopicPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const theme = useTheme();
  const toast = useToast();

  const { spaceVars } = useSpaceContext();
  const { layers } = useLayerContext();

  const { loading: viewerLoading, data: viewerData } = useQuery(VIEWER, {
    skip: !localStorage.getItem('token'),
  });
  const { loading: topicLoading, data: topicData } = useQuery(TOPIC, {
    variables: { spaceSlug: params.s, publicId: parseInt(params.t) },
  });

  const [pinTopic] = useMutation(PIN_TOPIC);
  const [unpinTopic] = useMutation(UNPIN_TOPIC);

  const [updateTopicState] = useMutation(UPDATE_TOPIC_STATE);

  const [isAssignTopicModalOpen, setIsAssignTopicModalOpen] = useState(false);
  const [assignee, setAssignee] = useState();

  const [isDeleteCommentModalOpen, setIsDeleteCommentModalOpen] =
    useState(false);
  const [isArchiveTopicModalOpen, setIsArchiveTopicModalOpen] = useState(false);

  const [isNewCommentModalOpen, setisNewCommentModalOpen] = useState(false);
  const [isEditCommentBodyModalOpen, setIsEditCommentBodyModalOpen] =
    useState(false);
  const [isEditTopicModalOpen, setIsEditTopicModalOpen] = useState(false);
  const [isVersionsModalOpen, setIsVersionsModalOpen] = useState(false);
  const [commentId, setCommentId] = useState();
  const [commentBody, setCommentBody] = useState();
  const [topicId, setTopicId] = useState();
  const [topicName, setTopicName] = useState();
  const [topicTags, setTopicTags] = useState();
  const [spaceSlug, setSpaceSlug] = useState();

  const search = new URLSearchParams();

  if (location.state?.filters?.author) {
    search.append('author', location.state?.filters?.author);
  }

  if (location.state?.filters?.state) {
    search.append('state', location.state?.filters?.state);
  }

  if (location.state?.filters?.assignee) {
    search.append('assignee', location.state?.filters?.assignee);
  }

  if (location.state?.filters?.tag) {
    search.append('tag', location.state?.filters?.tag);
  }

  const [keys, setKeys] = useState([]);

  // Handle topic keyboard shortcuts
  useEffect(() => {
    const onFocusHandler = () => {
      setKeys([]);
    };

    const onKeyUpHandler = (e) => {
      setKeys([...keys.filter((key) => key !== e.key)]);
    };

    const onKeyDownHandler = (e) => {
      setKeys([...keys, e.key]);

      if (
        !keys.includes('Control') &&
        !keys.includes('Shift') &&
        !keys.includes('Alt') &&
        !keys.includes('v') &&
        topicData &&
        layers.length === 0
      ) {
        if (e.key === 'Escape') {
          // Escape

          navigate(
            topicData.topic.archivedAt && location?.state?.fromArchives
              ? `/s/${params.s}/settings/archives${search && `?${search}`}`
              : `/s/${topicData.topic.space.slug}${search && `?${search}`}`
          );
        } else if (
          e.key === 'e' &&
          viewerData.viewer.id === topicData.topic.author.id
        ) {
          // Edit

          e.preventDefault();
          setTopicId(topicData.topic.id);
          setTopicName(topicData.topic.name);
          setCommentId(topicData.topic.comments[0].id);
          setCommentBody(topicData.topic.comments[0].body);
          setTopicTags(topicData.topic.tags);
          setSpaceSlug(topicData.topic.space.slug);
          setIsEditTopicModalOpen(true);
        } else if (
          e.key === 'a' &&
          (topicData.topic.space.role === 'ADMIN' ||
            viewerData.viewer.id === topicData.topic.author.id)
        ) {
          // Archive

          e.preventDefault();
          setTopicId(topicData.topic.id);
          setSpaceSlug(topicData.topic.space.slug);
          setIsArchiveTopicModalOpen(true);
        } else if (e.key === 'r' && topicData.topic.space.role) {
          // Reply

          e.preventDefault();
          setTopicId(topicData.topic.id);
          setisNewCommentModalOpen(true);
        }
      }
    };

    window.addEventListener('focus', onFocusHandler);
    document.addEventListener('keyup', onKeyUpHandler);
    document.addEventListener('keydown', onKeyDownHandler);

    return () => {
      window.removeEventListener('focus', onFocusHandler);
      document.removeEventListener('keyup', onKeyUpHandler);
      document.removeEventListener('keydown', onKeyDownHandler);
    };
  }, [keys, layers]);

  const commentRef = useRef([]);
  const [searchParams] = useSearchParams();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const currentComment = commentRef.current;
    const searchComment = searchParams.get('comment-id');

    if (searchComment && currentComment[searchComment] && !scrolled) {
      setScrolled(true);
      currentComment[searchComment].scrollIntoView({ behavior: 'smooth' });
    }
  });

  const [copiedLinks, setCopiedLinks] = useState([]);

  const clearCopiedLink = (id) => {
    setTimeout(() => {
      setCopiedLinks((links) => links.filter((l) => l !== id));
    }, 2500);
  };

  return (
    <>
      <Section>
        {viewerLoading || topicLoading ? (
          <>
            <Loader />
          </>
        ) : (
          <>
            <Helmet>
              <title>{topicData?.topic?.name}</title>
            </Helmet>
            {topicData && topicData.topic?.id ? (
              <>
                <SectionLink
                  to={{
                    pathname:
                      topicData.topic.archivedAt &&
                      location?.state?.fromArchives
                        ? `/s/${params.s}/settings/archives`
                        : `/s/${topicData.topic.space.slug}`,
                    search: `?${search}`,
                  }}
                  aria-label="Close topic"
                >
                  <Icon name="exit" />
                </SectionLink>
                <SectionName>
                  <Grid gap={theme.space[2]}>
                    {topicData.topic?.archivedAt ? (
                      <Inline aria-label="Archived topic">
                        <Icon name="archive" />
                      </Inline>
                    ) : (
                      <Inline
                        aria-label={
                          topicData.topic.isPinned
                            ? 'Pinned topic'
                            : 'Basic topic'
                        }
                      >
                        {topicData.topic.isPinned ? (
                          <Icon name="pin" />
                        ) : (
                          <Icon name="topic" />
                        )}
                      </Inline>
                    )}
                    <Inline aria-label={topicData.topic.state}>
                      {topicData.topic.state === 'Closed' ? (
                        <Icon name="state-closed" />
                      ) : (
                        <Icon name="state" />
                      )}
                    </Inline>
                    <Inline>{topicData.topic.name}</Inline>
                  </Grid>
                  <Grid gap={theme.space[2]}>
                    <Text
                      isSubtle
                      aria-label={`ID: ${topicData.topic.publicId}`}
                    >
                      {topicData.topic.publicId}
                    </Text>
                    {topicData.topic.assignee?.name && (
                      <Tooltip
                        text={`Assigned to ${topicData.topic.assignee.name}`}
                      >
                        <Avatar
                          src={topicData.topic.assignee.avatarUrl}
                          isSmall
                        />
                      </Tooltip>
                    )}
                  </Grid>
                </SectionName>
                {topicData.topic.tags.length !== 0 && (
                  <SectionItem isSmall>
                    <Tags>
                      {topicData.topic.tags.map((tag) => (
                        <TagsItem key={tag.id}>{tag.name}</TagsItem>
                      ))}
                    </Tags>
                  </SectionItem>
                )}
                {topicData.topic.archivedAt && (
                  <SectionItem>
                    <Text isSubtle>This topic is archived.</Text>
                  </SectionItem>
                )}
                <SectionItem
                  ref={(ref) =>
                    (commentRef.current[topicData.topic.comments[0].publicId] =
                      ref)
                  }
                  id={topicData.topic.comments[0].publicId}
                  isSelected={
                    searchParams.get('comment-id') ==
                    topicData.topic.comments[0].publicId
                  }
                >
                  <Grid
                    gap={theme.space[3]}
                    style={{ flexGrow: 1, width: '100%' }}
                  >
                    <Avatar
                      src={topicData.topic.author.avatarUrl}
                      alt={`${topicData.topic.author.name} avatar`}
                    />
                    <Grid
                      gap={theme.space[2]}
                      col
                      style={{ width: 'calc(100% - 56px)' }}
                    >
                      <Grid gap={theme.space[1]} col>
                        <Grid gap={theme.space[1]}>
                          <Text>{topicData.topic.author.name}</Text>
                          <Tooltip text={getDate(topicData.topic.createdAt)}>
                            <Text isSubtle>
                              {getRelativeTime(topicData.topic.createdAt)}
                            </Text>
                          </Tooltip>
                          {topicData.topic.space.role &&
                            topicData.topic.comments[0].lastEdited && (
                              <A
                                as="button"
                                isSubtle
                                onClick={() => {
                                  setCommentId(topicData.topic.comments[0].id);
                                  setIsVersionsModalOpen(true);
                                }}
                              >
                                edited{' '}
                                {getRelativeTime(
                                  topicData.topic.comments[0].lastEdited
                                )}
                              </A>
                            )}
                        </Grid>
                        <div style={{ overflowWrap: 'anywhere' }}>
                          {markdown.render(topicData.topic.comments[0].body)}
                        </div>
                      </Grid>
                      <Grid
                        style={{
                          marginLeft: `-${theme.space[2]}`,
                        }}
                      >
                        {viewerData &&
                        !topicData.topic.archivedAt &&
                        topicData.topic.space.role ? (
                          <>
                            {viewerData.viewer.id ===
                              topicData.topic.author.id && (
                              <>
                                <NavLink
                                  as="button"
                                  tooltip="Edit"
                                  tooltipPlacement="bottom"
                                  onClick={() => {
                                    setTopicId(topicData.topic.id);
                                    setTopicName(topicData.topic.name);
                                    setCommentId(
                                      topicData.topic.comments[0].id
                                    );
                                    setCommentBody(
                                      topicData.topic.comments[0].body
                                    );
                                    setTopicTags(topicData.topic.tags);
                                    setSpaceSlug(topicData.topic.space.slug);
                                    setIsEditTopicModalOpen(true);
                                  }}
                                >
                                  <Icon name="update" />
                                </NavLink>
                              </>
                            )}
                            {(topicData.topic.space.role === 'ADMIN' ||
                              viewerData.viewer.id ===
                                topicData.topic.author.id) && (
                              <NavLink
                                as="button"
                                tooltip="Archive"
                                tooltipPlacement="bottom"
                                onClick={() => {
                                  setTopicId(topicData.topic.id);
                                  setSpaceSlug(topicData.topic.space.slug);
                                  setIsArchiveTopicModalOpen(true);
                                }}
                              >
                                <Icon name="archive" />
                              </NavLink>
                            )}
                            <NavLink
                              as="button"
                              tooltip="Reply"
                              tooltipPlacement="bottom"
                              onClick={() => {
                                setTopicId(topicData.topic.id);
                                setisNewCommentModalOpen(true);
                              }}
                            >
                              <Icon name="reply" />
                            </NavLink>
                            {copiedLinks.includes(
                              topicData.topic.comments[0].publicId
                            ) ? (
                              <NavLink
                                as="button"
                                tooltip="Copied"
                                tooltipPlacement="bottom"
                              >
                                <Icon name="check" />
                              </NavLink>
                            ) : (
                              <NavLink
                                as="button"
                                tooltip="Copy link"
                                tooltipPlacement="bottom"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    `${document.location.origin}/s/${params.s}/t/${topicData.topic.publicId}?comment-id=${topicData.topic.comments[0].publicId}`
                                  );
                                  setCopiedLinks([
                                    ...copiedLinks,
                                    topicData.topic.comments[0].publicId,
                                  ]);
                                  clearCopiedLink(
                                    topicData.topic.comments[0].publicId
                                  );
                                }}
                              >
                                <Icon name="link" />
                              </NavLink>
                            )}
                            {topicData.topic.space.role === 'ADMIN' && (
                              <Drawer>
                                <DrawerTrigger>
                                  <NavLink
                                    as="button"
                                    tooltip="More options"
                                    tooltipPlacement="bottom"
                                  >
                                    <Icon name="more" />
                                  </NavLink>
                                </DrawerTrigger>
                                <DrawerList>
                                  <DrawerExit />
                                  {!topicData.topic.isPinned ? (
                                    <DrawerLink
                                      as="button"
                                      onClick={async () => {
                                        try {
                                          await pinTopic({
                                            variables: {
                                              id: topicData.topic.id,
                                            },
                                            update: (cache) => {
                                              const data = cache.readQuery({
                                                query: SPACE,
                                                variables: {
                                                  slug: params.s,
                                                  ...spaceVars,
                                                },
                                              });
                                              const space = {
                                                ...data.space,
                                                topics: [
                                                  ...data.space.topics,
                                                ].sort((a, b) => {
                                                  if (a.isPinned) {
                                                    return -1;
                                                  } else if (b.isPinned) {
                                                    return 1;
                                                  }

                                                  return (
                                                    b.createdAt - a.createdAt
                                                  );
                                                }),
                                              };
                                              cache.writeQuery({
                                                query: SPACE,
                                                data: { space },
                                                variables: {
                                                  slug: params.s,
                                                  ...spaceVars,
                                                },
                                              });
                                            },
                                          });
                                          toast({ children: 'Topic pinned' });
                                        } catch (error) {
                                          toast({ children: error.message });
                                        }
                                      }}
                                    >
                                      Pin topic
                                    </DrawerLink>
                                  ) : (
                                    <DrawerLink
                                      as="button"
                                      onClick={async () => {
                                        try {
                                          await unpinTopic({
                                            variables: {
                                              id: topicData.topic.id,
                                            },
                                            update: (cache) => {
                                              const data = cache.readQuery({
                                                query: SPACE,
                                                variables: {
                                                  slug: params.s,
                                                  ...spaceVars,
                                                },
                                              });
                                              const space = {
                                                ...data.space,
                                                topics: [
                                                  ...data.space.topics,
                                                ].sort(
                                                  (a, b) =>
                                                    b.createdAt - a.createdAt
                                                ),
                                              };
                                              cache.writeQuery({
                                                query: SPACE,
                                                data: { space },
                                                variables: {
                                                  slug: params.s,
                                                  ...spaceVars,
                                                },
                                              });
                                            },
                                          });
                                          toast({
                                            children: 'Topic unpinned',
                                          });
                                        } catch (error) {
                                          toast({ children: error.message });
                                        }
                                      }}
                                    >
                                      Unpin topic
                                    </DrawerLink>
                                  )}
                                  <DrawerLink
                                    as="button"
                                    onClick={async () => {
                                      try {
                                        await updateTopicState({
                                          variables: {
                                            id: topicData.topic.id,
                                            state:
                                              (topicData.topic.state ===
                                                'Closed' &&
                                                'OPEN') ||
                                              (topicData.topic.state ===
                                                'Open' &&
                                                'CLOSED'),
                                          },
                                        });
                                      } catch (error) {
                                        toast({ children: error.message });
                                      }
                                    }}
                                  >
                                    {topicData.topic.state === 'Open' &&
                                      'Close topic'}
                                    {topicData.topic.state === 'Closed' &&
                                      'Open topic'}
                                  </DrawerLink>
                                  <DrawerLink
                                    as="button"
                                    onClick={() => {
                                      setTopicId(topicData.topic.id);
                                      setAssignee(topicData.topic.assignee);
                                      setIsAssignTopicModalOpen(true);
                                    }}
                                  >
                                    Assign topic
                                  </DrawerLink>
                                </DrawerList>
                              </Drawer>
                            )}
                          </>
                        ) : (
                          <>
                            {copiedLinks.includes(
                              topicData.topic.comments[0].publicId
                            ) ? (
                              <NavLink
                                as="button"
                                tooltip="Copied"
                                tooltipPlacement="bottom"
                              >
                                <Icon name="check" />
                              </NavLink>
                            ) : (
                              <NavLink
                                as="button"
                                tooltip="Copy link"
                                tooltipPlacement="bottom"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    `${document.location.origin}/s/${params.s}/t/${topicData.topic.publicId}?comment-id=${topicData.topic.comments[0].publicId}`
                                  );
                                  setCopiedLinks([
                                    ...copiedLinks,
                                    topicData.topic.comments[0].publicId,
                                  ]);
                                  clearCopiedLink(
                                    topicData.topic.comments[0].publicId
                                  );
                                }}
                              >
                                <Icon name="link" />
                              </NavLink>
                            )}
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </SectionItem>
                {topicData.topic.comments.length !== 0 && (
                  <>
                    {topicData.topic.comments.slice(1).map((comment) => (
                      <SectionItem
                        ref={(ref) =>
                          (commentRef.current[comment.publicId] = ref)
                        }
                        key={comment.id}
                        id={comment.publicId}
                        isSelected={
                          searchParams.get('comment-id') == comment.publicId
                        }
                      >
                        <Grid
                          gap={theme.space[3]}
                          style={{ flexGrow: 1, width: '100%' }}
                        >
                          <Avatar
                            src={comment.author.avatarUrl}
                            alt={`${comment.author.name} avatar`}
                          />
                          <Grid
                            gap={theme.space[1]}
                            col
                            style={{ width: 'calc(100% - 56px)' }}
                          >
                            <Grid gap={theme.space[1]}>
                              <Inline>{comment.author.name}</Inline>
                              <Tooltip text={getDate(comment.createdAt)}>
                                <Text isSubtle>
                                  {getRelativeTime(comment.createdAt)}
                                </Text>
                              </Tooltip>
                              {topicData.topic.space.role &&
                                comment.lastEdited && (
                                  <A
                                    as="button"
                                    isSubtle
                                    onClick={() => {
                                      setCommentId(comment.id);
                                      setIsVersionsModalOpen(true);
                                    }}
                                  >
                                    edited {getRelativeTime(comment.lastEdited)}
                                  </A>
                                )}
                            </Grid>
                            <div style={{ overflow: 'hidden' }}>
                              {markdown.render(comment.body)}
                            </div>
                            <Grid
                              columns="max-content max-content"
                              style={{
                                marginLeft: `-${theme.space[2]}`,
                              }}
                            >
                              {viewerData &&
                                !topicData.topic.archivedAt &&
                                viewerData.viewer.id === comment.author.id && (
                                  <>
                                    <NavLink
                                      as="button"
                                      tooltip="Edit"
                                      tooltipPlacement="bottom"
                                      onClick={() => {
                                        setCommentId(comment.id);
                                        setCommentBody(comment.body);
                                        setIsEditCommentBodyModalOpen(true);
                                      }}
                                    >
                                      <Icon name="update" />
                                    </NavLink>
                                    <NavLink
                                      as="button"
                                      tooltip="Delete"
                                      tooltipPlacement="bottom"
                                      onClick={() => {
                                        setCommentId(comment.id);
                                        setIsDeleteCommentModalOpen(true);
                                      }}
                                    >
                                      <Icon name="delete" />
                                    </NavLink>
                                  </>
                                )}
                              {copiedLinks.includes(comment.publicId) ? (
                                <NavLink
                                  as="button"
                                  tooltip="Copied"
                                  tooltipPlacement="bottom"
                                >
                                  <Icon name="check" />
                                </NavLink>
                              ) : (
                                <NavLink
                                  as="button"
                                  tooltip="Copy link"
                                  tooltipPlacement="bottom"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      `${document.location.origin}/s/${params.s}/t/${topicData.topic.publicId}?comment-id=${comment.publicId}`
                                    );
                                    setCopiedLinks([
                                      ...copiedLinks,
                                      comment.publicId,
                                    ]);
                                    clearCopiedLink(comment.publicId);
                                  }}
                                >
                                  <Icon name="link" />
                                </NavLink>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </SectionItem>
                    ))}
                  </>
                )}
              </>
            ) : (
              <>
                <SectionItem>
                  There is no topic with that identifier.
                </SectionItem>
              </>
            )}
          </>
        )}
      </Section>
      {isAssignTopicModalOpen && (
        <AssignTopicModal
          assignee={assignee}
          topicId={topicId}
          onClose={() => setIsAssignTopicModalOpen(false)}
        />
      )}
      {isNewCommentModalOpen && (
        <NewCommentModal
          topicId={topicId}
          onClose={() => setisNewCommentModalOpen(false)}
        />
      )}
      {isEditCommentBodyModalOpen && (
        <EditCommentBodyModal
          commentId={commentId}
          commentBody={commentBody}
          onClose={() => setIsEditCommentBodyModalOpen(false)}
        />
      )}
      {isEditTopicModalOpen && (
        <EditTopicModal
          topicId={topicData?.topic?.id}
          topicName={topicData?.topic?.name}
          commentId={topicData?.topic?.comments[0]?.id}
          commentBody={topicData?.topic?.comments[0]?.body}
          topicTags={topicData?.topic?.tags}
          spaceSlug={params.s}
          onClose={() => setIsEditTopicModalOpen(false)}
        />
      )}
      {isDeleteCommentModalOpen && (
        <DeleteCommentModal
          commentId={commentId}
          onClose={() => setIsDeleteCommentModalOpen(false)}
        />
      )}
      {isArchiveTopicModalOpen && (
        <ArchiveTopicModal
          topicId={topicId}
          spaceSlug={spaceSlug}
          onClose={() => setIsArchiveTopicModalOpen(false)}
        />
      )}
      {isVersionsModalOpen && (
        <VersionsModal
          commentId={commentId}
          onClose={() => setIsVersionsModalOpen(false)}
        />
      )}
    </>
  );
};

export default TopicPage;
